/**
* EventObserver - a register module used to subscribe to and publish events.
* This can be used for both internal and external events. Each event type has
* its own event queue to help prevent name colisions between different event
* types.

* Event Types:
*   - Internal - used by the internal javascript. This is used to notify other
*                internal. Internal events can create historic or average events.
*   - Passive - Merchant Facing events. This is used when cardinal is pulling
*               merchant behavior or returning control back to the merchant. This
*               can be used to load in merchant configurations as well. this is
*               used with the external 'on' command.
*   - Active - Merchant facing event. This is used when the merchant it telling
*              us to do something. This is used w/ the external 'do' command.
*
**/

var _ = require('lodash'),
  log = require('../libraries/logger').getLoggerInstance('Base.Events'),
  RSVP = require('rsvp');

var internalEvents = {
    _type: 'internal'
  },
  externalActive = {
    _type: 'active'
  },
  externalPassive = {
    _type: 'passive'
  };

var privateMethods = {
  timeboxSubscribe: function (eventList, eventName, timeout) {
    return new RSVP.Promise(function (resolve, reject) {
      try {
        var eventCompleted = false,
          startTime = new Date(),
          timer, subscriptionCallback;

        timer = setTimeout(function () {
          if (!eventCompleted) {
            log.error('Time boxed event exceeded timeout after [' + (new Date().getTime() - startTime.getTime()) + ']');
            reject('timeout');
          }
        }, timeout);

        subscriptionCallback = function () {
          if (!eventCompleted) {
            log.debug('time boxed event completed in [' + (new Date().getTime() - startTime.getTime()) + ']ms');
            clearTimeout(timer);
            resolve.apply(this, arguments);
          } else {
            log.debug('Timeboxed event completed [' + (new Date().getTime - startTime.getTime()) - timeout + ']ms after configured wait time');
          }
        }

        privateMethods.subscribe(eventList, eventName, subscriptionCallback);
      } catch (error) {
        reject(error)
      }
    })
  },
  /**
   * Listen to an event to trigger a function or pass data
   * @param {Array} eventList - The event queue that should be used, internal, externalActive, externalPassive
   * @param {string} eventName - The namespace of the event you want to listen to
   * @param {object | function | string} data - What you want to pass or run when the namespace event triggers
   */
  subscribe: function (eventList, eventName, data) {
    // Validate the Event key before attempting anything
    if (typeof eventName !== 'string') {
      log.error('Invalid event [' + eventName + ']. Events must be of type string');
      return;
    }

    var eventObject = eventList[eventName],
      callback,
      config,
      newEntry,
      subscribers;

    if (!eventObject) {
      eventObject = eventList[eventName] = {};
      eventObject.subscribers = subscribers = [];
    } else {
      subscribers = eventObject.subscribers;
    }

    if (typeof data === 'function') {
      callback = data;
    } else if (typeof data === 'object') {
      config = data;
    } else {
      log.warn('Invalid data type passed for event [' + eventName + ']. Event will be skipped');
      return;
    }

    newEntry = {
      callback: callback,
      config: config
    };

    subscribers.push(newEntry);

    if (eventObject.historic && eventObject.completed && callback !== undefined) {
      log.debug('Historic event [' + eventName + '] already completed - resolving new subscriber');
      callback.apply(this, eventObject.args);
    }
  },
  /**
   * @param {Object} eventList - the object that should be used to look for subscribers
   * @param {string} eventName - namespace of event to fire
   * @param {boolean=} historic - boolean flag to determine if event should only fire once. Historic events will auto fire any subscribers who start listening after the event has been published.
   */
  publish: function (eventList, eventName, historic) {
    var deferred = RSVP.defer();
    try {
      var args = Array.prototype.slice.call(arguments, 3),
        configurationArgument,
        eventObject = eventList[eventName],
        subscriberDeferred = [];

      // If no event has been setup for this eventName then we have no subscribers and nothing to do -- unless its a historic event, then we need to mark that its completed so any late subscribers know
      if (!eventObject && !historic) {
        return new RSVP.Promise(function (resolve) {
          resolve();
        });
      } else if (!eventObject) {
        eventObject = eventList[eventName] = {};
        eventObject.subscribers = [];
        eventObject.historic = historic || false;
      } else {
        eventObject.historic = historic;
      }

      log.debug('Publishing [' + eventList._type + '] event [' + eventName + '] as ' + (historic ? 'a historic' : 'an average') + ' event with [' + eventObject.subscribers.length + '] subscriber(s)');

      _.each(eventObject.subscribers, function (subscriberAction) {
        if (subscriberAction.callback !== undefined) {
          try {
            subscriberDeferred.push(subscriberAction.callback.apply(this, args));
          } catch (e) {
            log.error('Event [' + eventName + '] callback resulted in an exception: ' + e);
          }
        } else if (subscriberAction.config !== undefined) {
          configurationArgument = subscriberAction.config;
        }
      });

      if (historic) {
        eventObject.completed = true;
        eventObject.args = args;
      }

      RSVP.all(subscriberDeferred)
        .then(function (data) {
          if (configurationArgument) {
            // append the configuration to the end of the completed arg array. Config will always be last option
            data.push(configurationArgument);
          }
          if (data.length === 1 && data[0] !== undefined) {
            deferred.resolve(data[0]);
          } else {
            deferred.resolve(data);
          }
        });

    } catch (error) {
      log.error("Failed while attempting to publish an event", error)
      deferred.reject(error);
    }

    return deferred.promise;
  },
  /**
   * Remove a subscriber from an event
   * @param {Object} eventList - the object that should be used to look for subscribers
   * @param {string} eventName - namespace of event to fire
   */
  removeEvent: function (eventList, eventName) {
    try {
      if (eventList[eventName] !== undefined) {
        log.debug("Removing [" + eventList[eventName].subscribers.length + "] event listener(s) for event [" + eventName + "]");
        delete eventList[eventName];
      } else {
        log.debug("No listener found for event [" + eventName + "] to remove")
      }
    } catch (error) {
      log.error("Failed to remove event [" + eventName + "] " + error);
    }
  }
};

// All the event based callbacks work the same way, we just use different
// collections to store the different types.
var publicMethods = {
  internal: {
    subscribe: _.partial(privateMethods.subscribe, internalEvents),
    publish: _.partial(privateMethods.publish, internalEvents),
    removeEvent: _.partial(privateMethods.removeEvent, internalEvents),
    timeboxSubscribe: _.partial(privateMethods.timeboxSubscribe, internalEvents),
    _type: 'internal'
  },
  passive: {
    subscribe: _.partial(privateMethods.subscribe, externalPassive),
    publish: _.partial(privateMethods.publish, externalPassive, _, false),
    removeEvent: _.partial(privateMethods.removeEvent, externalPassive),
    _type: 'passive'
  },
  active: {
    subscribe: _.partial(privateMethods.subscribe, externalActive),
    publish: _.partial(privateMethods.publish, externalActive, _, true),
    removeEvent: _.partial(privateMethods.removeEvent, externalActive),
    _type: 'active'
  }
};

module.exports = publicMethods.internal;
module.exports.passive = publicMethods.passive;
module.exports.active = publicMethods.active;


//////////////////
// WEBPACK FOOTER
// ./events/eventObserver.js
// module id = 14
// module chunks = 0 5 20